/**
 * PremiumSkeleton - Skeleton loader with glassmorphism for loading states
 * Provides elegant loading placeholders with premium visual style
 */
import { Box, Skeleton, keyframes } from '@mui/material';
import { usePremiumTokens } from '../theme/usePremiumTokens';

// Shimmer animation for premium effect
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

/**
 * Premium skeleton variants:
 * - text: Single line of text
 * - title: Larger title text
 * - avatar: Circular avatar placeholder
 * - card: Full card with content
 * - table: Table row skeleton
 * - chart: Chart/graph placeholder
 */
const PremiumSkeleton = ({
  variant = 'text',
  width,
  height,
  count = 1,
  spacing = 1,
  animation = 'shimmer', // 'shimmer' | 'pulse' | 'wave'
  ...props
}) => {
  const tokens = usePremiumTokens();

  // Base styles for all skeletons
  const baseStyles = {
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.surface.glass,
    ...(animation === 'shimmer' && {
      background: `linear-gradient(
        90deg,
        ${tokens.surface.glass} 25%,
        ${tokens.surface.hover} 50%,
        ${tokens.surface.glass} 75%
      )`,
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s ease-in-out infinite`,
    }),
  };

  // Variant-specific configurations
  const variants = {
    text: {
      width: width || '100%',
      height: height || 16,
    },
    title: {
      width: width || '60%',
      height: height || 28,
      borderRadius: tokens.radius.sm,
    },
    avatar: {
      width: width || 40,
      height: height || 40,
      borderRadius: '50%',
    },
    button: {
      width: width || 100,
      height: height || 36,
      borderRadius: tokens.radius.md,
    },
    card: {
      width: width || '100%',
      height: height || 120,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.border.subtle}`,
    },
    table: {
      width: width || '100%',
      height: height || 48,
      borderRadius: tokens.radius.sm,
    },
    chart: {
      width: width || '100%',
      height: height || 200,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.border.subtle}`,
    },
    thumbnail: {
      width: width || 80,
      height: height || 80,
      borderRadius: tokens.radius.md,
    },
  };

  const variantStyles = variants[variant] || variants.text;

  // Render multiple skeletons if count > 1
  if (count > 1) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
        {Array.from({ length: count }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            animation={animation === 'shimmer' ? false : animation}
            sx={{
              ...baseStyles,
              ...variantStyles,
              // Vary widths for text to look more natural
              ...(variant === 'text' && index === count - 1 && {
                width: '70%',
              }),
            }}
            {...props}
          />
        ))}
      </Box>
    );
  }

  return (
    <Skeleton
      variant={variant === 'avatar' ? 'circular' : 'rectangular'}
      animation={animation === 'shimmer' ? false : animation}
      sx={{
        ...baseStyles,
        ...variantStyles,
      }}
      {...props}
    />
  );
};

/**
 * Pre-composed skeleton layouts for common use cases
 */

// Card skeleton with title, text, and action
export const CardSkeleton = ({ ...props }) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: tokens?.radius?.lg || '12px',
        backgroundColor: tokens?.surface?.card || 'rgba(22, 27, 34, 0.6)',
        border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      {...props}
    >
      <PremiumSkeleton variant="title" width="50%" />
      <Box sx={{ mt: 2 }}>
        <PremiumSkeleton variant="text" count={3} spacing={0.75} />
      </Box>
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <PremiumSkeleton variant="button" width={80} />
        <PremiumSkeleton variant="button" width={80} />
      </Box>
    </Box>
  );
};

// Table row skeleton
export const TableRowSkeleton = ({ columns = 5, ...props }) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        borderBottom: `1px solid ${tokens.border.subtle}`,
      }}
      {...props}
    >
      <PremiumSkeleton variant="avatar" width={32} height={32} />
      {Array.from({ length: columns - 1 }).map((_, index) => (
        <Box key={index} sx={{ flex: 1 }}>
          <PremiumSkeleton
            variant="text"
            width={index === 0 ? '80%' : '60%'}
            height={14}
          />
        </Box>
      ))}
    </Box>
  );
};

// List skeleton
export const ListSkeleton = ({ rows = 5, ...props }) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        borderRadius: tokens.radius.lg,
        backgroundColor: tokens.surface.card,
        border: `1px solid ${tokens.border.subtle}`,
        overflow: 'hidden',
      }}
      {...props}
    >
      {Array.from({ length: rows }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderBottom: index < rows - 1 ? `1px solid ${tokens.border.subtle}` : 'none',
          }}
        >
          <PremiumSkeleton variant="avatar" />
          <Box sx={{ flex: 1 }}>
            <PremiumSkeleton variant="text" width="40%" height={14} />
            <Box sx={{ mt: 0.5 }}>
              <PremiumSkeleton variant="text" width="70%" height={12} />
            </Box>
          </Box>
          <PremiumSkeleton variant="button" width={60} height={28} />
        </Box>
      ))}
    </Box>
  );
};

// Dashboard stats skeleton
export const StatsSkeleton = ({ count = 4, ...props }) => {
  const tokens = usePremiumTokens();

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} {...props}>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            flex: '1 1 200px',
            p: 2,
            borderRadius: tokens?.radius?.lg || '12px',
            backgroundColor: tokens?.surface?.card || 'rgba(22, 27, 34, 0.6)',
            border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          <PremiumSkeleton variant="text" width={60} height={12} />
          <Box sx={{ mt: 1 }}>
            <PremiumSkeleton variant="title" width={100} height={32} />
          </Box>
          <Box sx={{ mt: 1 }}>
            <PremiumSkeleton variant="text" width="80%" height={10} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Chart skeleton
export const ChartSkeleton = ({ height = 300, ...props }) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: tokens?.radius?.lg || '12px',
        backgroundColor: tokens?.surface?.card || 'rgba(22, 27, 34, 0.6)',
        border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      {...props}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <PremiumSkeleton variant="title" width={150} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <PremiumSkeleton variant="button" width={60} height={28} />
          <PremiumSkeleton variant="button" width={60} height={28} />
        </Box>
      </Box>
      <PremiumSkeleton variant="chart" height={height - 80} />
    </Box>
  );
};

export default PremiumSkeleton;
